import React, { useRef } from "react";
import { SelectPicker } from "rsuite";
import { Button, Col, FormGroup, Label, Row } from "reactstrap";
import PropTypes from "prop-types";
import TagPicker from "../../common/tagPicker/TagPicker";

/**
 * ApplyToSection - "Apply To" section of OfferType form
 * Contains Properties TagPicker (for regular promos) and Rooms & Rate Plans selector
 */
function ApplyToSection({
  readOnly = false,
  isMarket = false,
  isEdit = false,
  selectedProperty,
  selectedProperties = [],
  onPropertiesChange,
  onPropertiesBlur = () => {},
  propertyOptions = [],
  onPropertiesSearch = () => {},
  onPropertiesReset = () => {},
  roomsRateplansValue,
  onRoomsRateplansChange,
  roomsRateplansOptions = [],
  onSelectClick,
  buttonText = "Select",
  buttonClassName = "select-btn",
  isButtonDisabled = false,
  onFieldChange,
}) {
  const propertiesContainerRef = useRef(null);
  const roomsRateplansContainerRef = useRef(null);

  const handleRoomsRateplansValueChange = (value) => {
    onRoomsRateplansChange(value);
    onFieldChange("roomsRateplansMode", value);
    if (value === "all") {
      // Clear all selections - set allowedRateRoomCodes to empty array
      onFieldChange("allowedRateRoomCodes", []);
    } else if (value === "select") {
      // Clear auto-generated combos to require drawer interaction
      onFieldChange("allowedRateRoomCodes", []);
      onFieldChange("allowedRateRoomCodesByProperty", {});
    }
  };

  return (
    <>
      {/* Apply To Section Header */}
      <Row className="mt-3">
        <Col md={6}>
          <h4 className="form-section-subtitle mb-3">Apply To</h4>
        </Col>
      </Row>

      {isMarket ? (
        // Smart Promo Layout - Only Rooms & Rate Plans with Select button
        <Row>
          <Col md={12}>
            <FormGroup>
              <Label for="roomsRateplans">Rooms & Rate Plans</Label>
              <div className="room-rateplans-dropdowns">
                <div
                  className="flex-grow-1 position-relative"
                  ref={roomsRateplansContainerRef}
                >
                  <SelectPicker
                    id="roomsRateplans"
                    value={roomsRateplansValue}
                    onChange={handleRoomsRateplansValueChange}
                    data={roomsRateplansOptions}
                    placeholder="Select option"
                    block
                    placement="topStart"
                    searchable={false}
                    cleanable={false}
                    container={() => roomsRateplansContainerRef.current}
                  />
                </div>
                <Button
                  color="secondary"
                  onClick={onSelectClick}
                  className={`select-btn ${isMarket ? "select-btn-no-margin" : ""}`}
                  disabled={false}
                >
                  Select
                </Button>
              </div>
            </FormGroup>
          </Col>
        </Row>
      ) : (
        // Regular Layout - Properties and Rooms & Rate Plans with View button
        <Row className="apply-to-row">
          {/* Properties section - hidden ONLY when isMarket is true */}
          {!isMarket && (
            <Col md={6}>
              <FormGroup>
                <Label for="properties">Properties</Label>
                <div className="position-relative" ref={propertiesContainerRef}>
                  <TagPicker
                    id="properties"
                    value={
                      isEdit && selectedProperty?.propertyId
                        ? [selectedProperty.propertyId]
                        : selectedProperties
                    }
                    onChange={(value) => {
                      const newValue = Array.isArray(value) ? value : [];
                      onPropertiesChange(newValue);
                      // Store selected properties in promo object for use in CreatePromo
                      onFieldChange("selectedProperties", newValue);
                    }}
                    onClose={onPropertiesBlur}
                    onSearch={onPropertiesSearch}
                    onSelect={onPropertiesReset}
                    data={propertyOptions}
                    placeholder="Select properties"
                    block
                    cleanable={false}
                    searchable
                    placement="topStart"
                    disabled={isEdit}
                    container={() => propertiesContainerRef.current}
                  />
                </div>
              </FormGroup>
            </Col>
          )}
          <Col md={6}>
            <div className="room-rateplans-dropdowns">
              {/* Rooms & Rate Plans - full width when isMarket is true, otherwise md={9} */}
              <Col md={isMarket ? 12 : 9}>
                <FormGroup>
                  <Label for="roomsRateplans">Rooms & Rate Plans</Label>
                  <div
                    className="position-relative"
                    ref={roomsRateplansContainerRef}
                  >
                    <SelectPicker
                      id="roomsRateplans"
                      className="rooms-rateplans-picker"
                      value={roomsRateplansValue}
                      onChange={handleRoomsRateplansValueChange}
                      data={roomsRateplansOptions}
                      placeholder="Select option"
                      placement="topStart"
                      block
                      searchable={false}
                      cleanable={false}
                      disabled={selectedProperties.length === 0}
                      container={() => roomsRateplansContainerRef.current}
                    />
                  </div>
                </FormGroup>
              </Col>
              {/* View/Select button - hidden when isMarket is true */}
              {!isMarket && (
                <Col md={3} className="d-flex align-items-end p-0">
                  {readOnly && !isButtonDisabled ? (
                    // EO-1538: a view-only promo still shows its rooms and rate plans.
                    // The form sits in a disabled <fieldset>, which disables every
                    // native button in it, so this renders as a span-button (not a
                    // form control) and re-enables pointer events past the
                    // read-only wrapper. The drawer it opens is read-only too.
                    <Button
                      tag="span"
                      role="button"
                      tabIndex={0}
                      color="secondary"
                      onClick={onSelectClick}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onSelectClick();
                        }
                      }}
                      className={`${buttonClassName} apply-to__view--readonly`}
                      data-testid="rooms-rateplans-view-readonly"
                    >
                      View
                    </Button>
                  ) : (
                    <Button
                      color="secondary"
                      onClick={onSelectClick}
                      className={buttonClassName}
                      disabled={isButtonDisabled}
                    >
                      {buttonText}
                    </Button>
                  )}
                </Col>
              )}
            </div>
          </Col>
        </Row>
      )}
    </>
  );
}

ApplyToSection.propTypes = {
  readOnly: PropTypes.bool,
  isMarket: PropTypes.bool,
  isEdit: PropTypes.bool,
  selectedProperty: PropTypes.shape({
    propertyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  selectedProperties: PropTypes.arrayOf(PropTypes.string),
  onPropertiesChange: PropTypes.func.isRequired,
  onPropertiesBlur: PropTypes.func,
  propertyOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  onPropertiesSearch: PropTypes.func,
  onPropertiesReset: PropTypes.func,
  roomsRateplansValue: PropTypes.string,
  onRoomsRateplansChange: PropTypes.func.isRequired,
  roomsRateplansOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  onSelectClick: PropTypes.func.isRequired,
  buttonText: PropTypes.string,
  buttonClassName: PropTypes.string,
  isButtonDisabled: PropTypes.bool,
  onFieldChange: PropTypes.func.isRequired,
};

export default ApplyToSection;
